import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { userAuthenticated } from './action/authenticationEvents';
import { fetchDepots, fetchThings } from './action/depotsCommands';
import { fetchLocations } from './action/locationsCommands';
import { getTokenData } from './auth0';
import TenantBootstrap from './layout/TenantBootstrap';
import MainLayout from './layout/MainLayout';
import VisitorBookingPage from './pages/bookings/BookingPage';
import LoadingPage from './pages/common/LoadingPage';
import SignInRedirectPage from './pages/common/SignInRedirectPage';
import Dashboard from './pages/Dashboard';
import DepotDetailsPage from './pages/depotDetails/DepotDetailsPage';
import DepotsPage from './pages/depots/DepotsPage';
import AccessManagementUpsell from './pages/depots/AccessManagementUpsell';
import AccessManagementUnavailable from './pages/depots/AccessManagementUnavailable';
import VouchersCodesPage from './pages/vouchersCodes/VouchersCodesPage';
import InvoicesPage from './pages/invoices/InvoicesPage';
import ServicesPage from './pages/services/ServicesPage';
import DepositsPage from './pages/deposits/DepositsPage';
import IssueReportDetailsPage from './pages/issueReportDetails/IssueReportDetailsPage';
import AddIssueReportPage from './pages/issueReports/AddIssueReportPage';
import IssueReportPage from './pages/issueReports/IssueReportsPage';
import LocationDetailsPage from './pages/locationDetails/LocationDetailsPage';
import LocationsPage from './pages/locations/LocationsPage';
import AddOnServices from './pages/rentals/AddOnServices';
import Apartment from './pages/rentals/Apartment';
import BookingDetailsPage from './pages/rentals/Bookings/BookingDetailsPage';
import BookingsArea from './pages/rentals/Bookings/BookingsArea';
import CreateRoomBookingPage from './pages/rentals/CreateBooking/CreateRoomBookingPage';
import Polices from './pages/rentals/Polices';
import VisitorBookingsPage from './pages/visitorBookings/VisitorBookingsPage';
import { useAuth0 } from './react-auth0-spa';
import { paths } from './routes';
import { makeHasPermissionSelector, makeHasRoleSelector, permissionsLoadedSelector, tenantSelector } from './selector/authenticationSelector';
import { setConfig, tenantSelected } from './store/slices/authenticationSlice';
import { RootState } from './store/store';
import { TenantConfig } from './tenants/config';
import { LEGACY_TENANT_SLUGS } from './api/types';
import { legacyTenantForPermission } from './common/legacyTenantPermissions';
import { useCanAccessAccount } from './hooks/useCanAccessAccount';
import { useAccessManagementEntitlement } from './hooks/useCanUseAccessManagement';
import { toAuthPayload } from './common/accountPermissions';
import { useLazyGetAccountPermissionsQuery, useLazyGetAccountQuery, useLazyGetTenantAccountQuery } from './api/account/accountApiSlice';
import { useIsMaster } from './common/useIsMaster';
import { isSelfServiceTenant } from './common/tenantStore';
import { applyTenantAccent } from './common/tenantAccentColor';
import { AccessManagementView, accessManagementView } from './utils/accessManagementGate';
import { isDeadSessionError } from './utils/authSession';
import {
    TenantService,
    getServicesForSolution,
    getStoredOrganizationName,
    getStoredServices,
    getStoredSolution,
    setStoredOrganizationName,
    setStoredServices,
    setStoredSolution,
} from './common/selfServiceSolutions';
import EditRoomBooking from './pages/rentals/EditBooking/EditRoomBooking';
import SearchBookingPage from './pages/bookings/SearchBookingPage';
import IssueReports from './pages/rentals/IssueReports/IssueReports';
import ApartmentRentalAddIssueReportPage from './pages/rentals/IssueReports/AddIssueReports';
import ApartmentRentalIssueReportDetailsPage from './pages/rentals/IssueReports/ViewIssueReport';
import Unauthorized from './pages/Unauthorized';
import NotFound from './pages/NotFound';
import Permission from './common/permissions';
import Roles from './common/roles';
import CreateVenueBookingPage from './pages/rentals/CreateBooking/CreateVenueBookingPage';
import EditVenueBookingPage from './pages/rentals/EditBooking/EditVenueBookingPage';
import AssetsOverviewPage from './pages/assets/AssetsOverviewPage';
import AssetDetailPage from './pages/assets/AssetDetailPage';
import AssetCreatePage from './pages/assets/AssetCreatePage';
import CreateStationPage from './pages/stations/createStation/CreateStationPage';
import StationDetailPage from './pages/stations/StationDetailPage';
import ProductDetailPage from './pages/products/ProductDetailPage';
import CreateNonRoomBookingPage from './pages/rentals/CreateBooking/CreateNonRoomBookingPage';
import { canSeeCustomersArea } from './common/customersAreaAccess';
import CustomersPage from './pages/customers/CustomersPage';
import OrganizationCreatePage from './pages/organizations/OrganizationCreatePage';
import OrganizationDetailPage from './pages/organizations/OrganizationDetailPage';
import CustomerAccountDetailPage from './pages/customers/CustomerAccountDetailPage';
import CreateSeriesBookingPage from './pages/rentals/CreateBooking/CreateSeriesBookingPage';
import SeriesBookingDetailsPage from './pages/rentals/Bookings/SeriesBookingDetailsPage';
import CreateBlockerPage from './pages/rentals/CreateBooking/CreateBlockerPage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import SignupGate from './pages/onboarding/SignupGate';
import VerifyEmailPage from './pages/onboarding/VerifyEmailPage';
import AccountPage from './pages/account/AccountPage';
import OnboardingImportPage from './pages/account/onboardingImport/OnboardingImportPage';
import OrganizationImportPage from './pages/organizations/import/OrganizationImportPage';
import HelpCenterPage from './pages/help/HelpCenterPage';
import HelpArticlePage from './pages/help/HelpArticlePage';
import MasterBookingsPage from './pages/master/MasterBookingsPage';
import MasterProblemReportsPage from './pages/master/MasterProblemReportsPage';
import MasterLockersPage from './pages/master/MasterLockersPage';
import MasterTenantsPage from './pages/master/MasterTenantsPage';
import ScrollToTop from './layout/ScrollToTop';
import DefaultLanding from './layout/DefaultLanding';
import { images } from './images/images';

const StatisticsPage = React.lazy(() => import('./pages/statistics/StatisticsPage'));

const PostLoginRedirect = () => {
    const navigate = useNavigate();
    useEffect(() => {
        const target = window.sessionStorage.getItem('postLoginTarget');
        if (target) {
            window.sessionStorage.removeItem('postLoginTarget');
            navigate(target, { replace: true });
        }
    }, [navigate]);
    return null;
};

/**
 * Renders one of the three access-management views. The decision itself — who
 * decides, who waits, and therefore which view — lives in
 * `utils/accessManagementGate.ts`, where it is under test; this is only the
 * mapping to elements, so there is no boolean here to get wrong.
 */
const ACCESS_MANAGEMENT_VIEWS: Record<AccessManagementView, () => React.ReactElement> = {
    loading: () => <LoadingPage />,
    devices: () => <DepotsPage />,
    upsell: () => <AccessManagementUpsell />,
    unavailable: () => <AccessManagementUnavailable />,
};

/**
 * The account screens (the account and the onboarding import): the tenant
 * owner, or a master viewing the tenant. Waits while the role resolves, so an
 * owner deep-linking here is not bounced before it loads.
 */
const AccountGate = ({ resolving, canAccess, children }: { resolving: boolean, canAccess: boolean, children: React.ReactElement }) => {
    if (resolving) return null;
    return canAccess ? children : <Navigate to={paths.unauthorized} replace />;
};

const App = () => {
    const dispatch = useDispatch();
    const { loading, isAuthenticated, loginWithRedirect, user, authError } = useAuth0();
    const [triggerGetPermissions] = useLazyGetAccountPermissionsQuery();
    const [triggerGetAccount] = useLazyGetAccountQuery();
    const [triggerGetTenantAccount] = useLazyGetTenantAccountQuery();
    const isMaster = useIsMaster();
    const hasShowBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.showBookings()),
    );
    const hasShowDashboardPermission = useSelector(
        makeHasPermissionSelector(Permission.showDashboard()),
    );
    const hasShowLockersPermission = useSelector(
        makeHasPermissionSelector(Permission.showLockers()),
    );
    // `isResolving` matters here for the same reason it does on /account:
    // answering "not entitled" while getAccount is still in flight shows a
    // tenant who pays for the add-on the sales pitch for it (ADR-0001
    // Amendment 4). But it is only worth waiting for where the entitlement is
    // what decides — see `shouldWaitForAccessManagementEntitlement`.
    const {
        entitled: canUseAccessManagement,
        isResolving: accessManagementResolving,
        hasFailed: accessManagementLookupFailed,
    } = useAccessManagementEntitlement();
    const selectedTenantSlug = useSelector(tenantSelector);
    const accessManagementCurrentView = accessManagementView(
        accessManagementResolving,
        hasShowLockersPermission,
        canUseAccessManagement,
        isSelfServiceTenant(selectedTenantSlug),
        accessManagementLookupFailed,
        isMaster,
    );
    const hasShowIssueReportsPermission = useSelector(
        makeHasPermissionSelector(Permission.showIssueReports()),
    );
    const hasCreateIssueReportsPermission = useSelector(
        makeHasPermissionSelector(Permission.createIssueReports()),
    );
    const hasReadVouchersPermission = useSelector(
        makeHasPermissionSelector(Permission.readVouchers()),
    );
    // The invoice list's own string, as its endpoint checks (`read:invoices`).
    const hasReadInvoicesPermission = useSelector(
        makeHasPermissionSelector(Permission.readInvoices()),
    );
    const hasCreateBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.createBookings()),
    );
    const hasManageAssetsPermission = useSelector(
        makeHasPermissionSelector(Permission.createAssets()),
    );
    const hasWriteCustomersPermission = useSelector(
        makeHasPermissionSelector(Permission.writeCustomers()),
    );
    // Each route on its own feature's string (PO, 2026-10-07): editing a
    // booking is `edit:bookings` (as its Edit buttons are), a station is the
    // depot read its backend route checks, a product is the catalogue's.
    const hasEditBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.editBookings()),
    );
    const hasReadDepotsPermission = useSelector(
        makeHasPermissionSelector(Permission.readDepots()),
    );
    const hasManageProductsPermission = useSelector(
        makeHasPermissionSelector(Permission.manageProducts()),
    );
    const permissionLoaded = useSelector(permissionsLoadedSelector);
    const isReceptionist = useSelector(
        makeHasRoleSelector(Roles.receptionist()),
    );

    const tenant = useSelector(tenantSelector);

    // The Services area — same gate as the sidebar entry (SideBarContent),
    // incl. deep links. The SUP-only gate the old "Rental products" page
    // carried is gone: Services is where every self-service tenant configures
    // its offers. It still covers legacy SUP tenants, whose deposit section is
    // the one thing this page holds for them.
    const tenantConfig = useSelector(
        (state: RootState) => state.authentication.config,
    );
    const hasSupService = (tenantConfig?.services ?? []).some(
        (service) => service.toLowerCase() === 'sup',
    );
    const canSeeServices = isSelfServiceTenant(tenant) || hasSupService;

    /**
     * Guard for the Customers routes. Organizations and customer accounts are
     * operational-only models — the endpoints answer 409 for a legacy tenant —
     * so the screens follow the same line rather than rendering over a wall of
     * failed requests.
     *
     * `!tenant` renders nothing rather than redirecting: on a hard reload the
     * routes mount before the tenant is restored into Redux, and bouncing to
     * unauthorized there would break a legitimate deep link (the same race the
     * account and services routes guard against).
     */
    const customerAccessGate = (element: React.ReactElement) => {
        if (!tenant) return null;
        return canSeeCustomersArea(tenant) ? element : <Navigate to={paths.unauthorized} replace />;
    };

    // Account screen is restricted to the tenant owner (edit) and masters
    // (read-only) — normal non-owner members are redirected away. Same guard
    // backs the sidebar nav item. `isResolving` keeps an owner deep-linking to
    // /account from being bounced before their role loads.
    const { canAccess: canAccessAccount, isResolving: accountAccessResolving } =
        useCanAccessAccount();

    // Guards the hand-over to Auth0 so it happens at most once per app load.
    // Two callers can reach it — the effect below and the token-refresh
    // failure path — and a second loginWithRedirect would overwrite the PKCE
    // transaction the first navigation is still committing.
    const loginRedirectFiredRef = useRef(false);
    // Set once the token call says the session is dead. Without it the app
    // stays on the bare loading spinner while the browser leaves for Auth0 —
    // `permissionsLoaded` deliberately never flips on that path — and, should
    // the redirect not happen, stays there for good: the white screen.
    const [sessionExpired, setSessionExpired] = useState(false);
    const redirectToLogin = () => {
        if (loginRedirectFiredRef.current) return;
        loginRedirectFiredRef.current = true;
        Promise.resolve(loginWithRedirect({})).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Auth0 login redirect failed', err);
            // Re-arm so the manual retry on the sign-in screen can run.
            loginRedirectFiredRef.current = false;
        });
    };

    useEffect(() => {
        // This effect re-runs when `tenant` resolves (see the dep array). Its
        // body is async, so without this guard a slower FIRST run (tenant still
        // null) can land AFTER the second and overwrite a correct grant with a
        // stale one — which locked self-service owners out of every
        // permission-gated surface on first login. Latest run wins.
        let cancelled = false;

        if (!loading && isAuthenticated) {
            getTokenData().then(async (data) => {
                if (cancelled) return;
                // Self-service users have no resource permissions in their
                // Auth0 JWT (Auth0 = authentication only). For them the UI
                // permission set comes from our DB via
                // GET /admin/account/permissions (the single source of truth),
                // not from the empty `data.permissions`.
                const savedTenant = tenant ?? localStorage.getItem('selectedTenant');
                // On a first login in a fresh browser neither Redux nor
                // localStorage has a tenant yet (TenantBootstrap hasn't resolved
                // the account). Falling back to "not self-service" there handed
                // the user their EMPTY JWT permission set, so the nav, the
                // bookings route and every other gate rendered as unauthorized.
                // Decide from the JWT instead: an internal/legacy user always
                // carries a `tenant:<slug>` grant or `master`; a self-service
                // user carries neither.
                const hasLegacyGrant = (data.permissions || []).some(
                    (perm) => !!legacyTenantForPermission(perm),
                );
                // A master (internal staff) keeps their real JWT roles +
                // permissions even when viewing a self-service tenant via the
                // switcher. The DB-driven grant below is only for genuine
                // self-service users — applying it to a master would strip the
                // `master` role and hide the TenantSwitcher, trapping them in
                // the selected tenant. Masters always carry `master` in the JWT.
                const isMasterJwt = (data.roles || []).some(
                    (r) => r.toLowerCase().includes('master'),
                );
                // Known tenant → decide by its slug, as before. Unknown tenant
                // → decide by the JWT (see above). Either way a master keeps
                // their JWT grant.
                const isSelfService = savedTenant
                    ? !LEGACY_TENANT_SLUGS.has(savedTenant)
                    : !hasLegacyGrant;
                const useSelfServiceGrant = isSelfService && !isMasterJwt;
                if (useSelfServiceGrant) {
                    try {
                        const perms = await triggerGetPermissions().unwrap();
                        if (cancelled) return;
                        dispatch(userAuthenticated(toAuthPayload(perms)));
                    } catch (err) {
                        if (cancelled) return;
                        // 404 = the caller has no `app_user` row in the
                        // operational DB (e.g. a legacy tenant's user — there are
                        // many). Fall back to their Auth0 JWT permissions/roles,
                        // exactly as legacy users are served, so removing the
                        // self-service UI bypasses can't lock a non-DB user out.
                        // Any other (transient) failure → empty grant so
                        // permissionsLoaded still flips and the app renders.
                        const status = (err as { status?: number })?.status;
                        if (status === 404) {
                            dispatch(userAuthenticated({
                                permissions: data.permissions,
                                roles: data.roles || [],
                            }));
                        } else {
                            dispatch(userAuthenticated({ permissions: [], roles: [] }));
                        }
                    }
                } else {
                    dispatch(
                        userAuthenticated({
                            permissions: data.permissions,
                            roles: data.roles || [],
                        }),
                    );
                }
                if (tenant) {
                    dispatch(fetchDepots({}));
                    dispatch(fetchLocations({}));
                    dispatch(fetchThings({}));
                }
            }).catch((err) => {
                if (cancelled) return;
                // getTokenSilently rejects when the session can no longer be
                // renewed (expired or revoked refresh token) or on a plain
                // network blip. Before this catch the rejection was unhandled:
                // `permissionsLoaded` never flipped and the app hung on a
                // loading screen forever.
                if (isDeadSessionError(err)) {
                    setSessionExpired(true);
                    // The session really is gone — sign in again rather than
                    // leaving a shell the user can't do anything in.
                    redirectToLogin();
                    return;
                }
                // Anything else (transient): render with an empty grant so the
                // app is usable and the user can retry or sign out by hand.
                dispatch(userAuthenticated({ permissions: [], roles: [] }));
            });
        }

        return () => {
            cancelled = true;
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [loading, isAuthenticated, tenant]);

    // Hand an unauthenticated visitor over to Auth0. Split out of the
    // permissions effect above (whose dep array carries `tenant`, irrelevant
    // here) so the redirect has one owner and one trigger condition.
    useEffect(() => {
        if (loading || isAuthenticated) return;
        // The /signup entry point is owned by SignupGate (rendered as a
        // route below). It runs its own allowlist check on
        // ?solution=… and only then calls loginWithRedirect — bailing
        // here keeps the bootstrap effect from racing it into Auth0
        // before the gate can render the unsupported-solution screen.
        if (window.location.pathname === paths.signup) return;
        if (authError) {
            // A failed round trip must NOT be retried automatically — that is
            // the redirect loop. Mark the automatic hand-over as spent so that
            // clearing the error (SignInRedirectPage's manual retry, which
            // does its own loginWithRedirect) can't fire a second, competing
            // redirect here.
            loginRedirectFiredRef.current = true;
            return;
        }
        redirectToLogin();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [loading, isAuthenticated, authError]);

    useEffect(() => {
        if (!tenant) {
            return;
        }

        // Self-service tenants pick their own slug — they don't have a
        // per-tenant folder under src/tenants/. Derive the services list
        // from the trial solution this tenant signed up for (stashed in
        // localStorage by OnboardingPage on register and refreshed by
        // TenantBootstrap on every getAccount). Falls back to ROOM if no
        // solution has been recorded yet (e.g. mid-onboarding before the
        // register response lands).
        //
        // Eventually services should come from the backend itself — see
        // the linked issue for surfacing tenant.services on getAccount so
        // the SOLUTION_TO_SERVICES map can be retired.
        if (!LEGACY_TENANT_SLUGS.has(tenant)) {
            const solution = getStoredSolution() ?? 'room';
            // The tenant's stored config.services (cached from getAccount in
            // TenantBootstrap) is the source of truth; fall back to the
            // solution map until it's been recorded.
            const services = getStoredServices() ?? getServicesForSolution(solution);
            const organizationName = getStoredOrganizationName() ?? undefined;
            const selfServiceConfig: TenantConfig = {
                services,
                logo: images.kolulaLogo,
                organizationName,
                createBookingOptions: services,
            };
            dispatch(setConfig(selfServiceConfig));
            dispatch(tenantSelected({ tenant }));
        } else {
            const loadConfig = async () => {
                try {
                    const config = (await import(`./tenants/${tenant}/config`)).config as TenantConfig;
                    dispatch(setConfig(config));
                    dispatch(tenantSelected({ tenant }));
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(`Error loading tenant config for ${tenant}`, err);
                }
            };
            loadConfig();
        }

        // eslint-disable-next-line no-console
        console.log('tenant', tenant);

        // Defaults for all tenants – individual cases below only override what differs
        document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
        document.documentElement.style.setProperty('--color-background', 'var(--color-default-background)');
        document.documentElement.style.setProperty('--color-foreground', 'var(--color-default-foreground)');
        document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
        document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');

        switch (tenant) {
            case 'segmenta':
                document.documentElement.style.setProperty('--color-primary', '166, 150, 255');
                document.documentElement.style.setProperty('--color-background', '22, 22, 22');
                document.documentElement.style.setProperty('--color-foreground', '255, 255, 255');
                document.documentElement.style.setProperty('--color-text-contrast', '#000000');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break;
            case 'vonovia':
            case 'vonovia_v2':
                document.documentElement.style.setProperty('--color-primary', '1, 154, 168');
                document.documentElement.style.setProperty('--color-background', 'var(--color-default-background)');
                document.documentElement.style.setProperty('--color-foreground', 'var(--color-default-foreground)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#ffffff');
                break;
            case 'kolula':
                document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break;                
            case 'demo':
                document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break;
            case 'kolula-key':
                document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break; 
            case 'dhu':
                document.documentElement.style.setProperty('--color-primary', '138, 34, 119');
                document.documentElement.style.setProperty('--color-background', 'var(--color-default-background)');
                document.documentElement.style.setProperty('--color-foreground', 'var(--color-default-foreground)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#ffffff');
                break;
            case 'neuland':
                document.documentElement.style.setProperty('--color-primary', '227, 6, 19');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#ffffff');
                break;
            case 'badesee-petershagen':
                document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break;
            case 'strandbad-pertisau':
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break;
            case 'seezeit':
                document.documentElement.style.setProperty('--color-primary', 'var(--color-default-primary)');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#000000');
                break;
            case 'segmenta_v2':
                document.documentElement.style.setProperty('--color-primary', '0, 0, 0');
                document.documentElement.style.setProperty('--color-text-contrast', 'var(--color-default-text-contrast)');
                document.documentElement.style.setProperty('--color-button-text-contrast', '#ffffff');
                break;
        }
    }, [tenant]);

    // Keep an operational tenant's config.services in sync on every tenant
    // change — not just at app boot. TenantBootstrap resolves the account once
    // (its ranRef one-shot guard) and the topbar TenantSwitcher doesn't re-fire
    // it, so switching to an operational tenant would otherwise leave
    // config.services on the previous tenant's value or the venue-only solution
    // fallback (getServicesForSolution('room')), and its non-venue use cases
    // (e.g. apartment) never load in the bookings list. The synthesis effect
    // above sets a cached/fallback config for the first paint; here we fetch the
    // real config and correct it. Legacy tenants load config from their bundled
    // file and are skipped.
    //
    // Endpoint choice matters: GET /admin/account resolves the tenant by the
    // caller's auth0_sub → a master viewing an operational tenant would get
    // their OWN tenant's config, not the selected one. So for masters we read
    // the *viewed* tenant via GET /admin/tenants/:slug/account (master-only,
    // resolved by slug). A self-service owner only ever has their own tenant
    // selected, so /account is correct for them.
    useEffect(() => {
        if (!tenant || LEGACY_TENANT_SLUGS.has(tenant)) return;
        let cancelled = false;

        const applyTenant = (t: any) => {
            // Ignore if the user switched away mid-flight, or the account isn't
            // a resolvable tenant yet (mid-onboarding).
            if (cancelled || !t?.slug) return;
            const services = (t.services as TenantService[] | null)
                ?? getServicesForSolution(t.solution);
            // Refresh the caches so a later synchronous read is correct too.
            setStoredServices(services);
            setStoredSolution(t.solution);
            if (t.organizationName) {
                setStoredOrganizationName(t.organizationName);
            }
            // The accent the tenant configured for its booking page is its
            // admin accent too; without one the default set above stays.
            applyTenantAccent(t.branding?.primaryColor);
            dispatch(setConfig({
                services,
                logo: images.kolulaLogo,
                organizationName:
                    t.organizationName ?? getStoredOrganizationName() ?? undefined,
                // The uploaded logo replaces the name as text in the top bar.
                brandLogoUrl: t.branding?.logoUrl || undefined,
                createBookingOptions: services,
            }));
        };

        if (isMaster) {
            triggerGetTenantAccount(tenant)
                .unwrap()
                .then((res) => applyTenant(res.tenant))
                .catch(() => {
                    // Keep the cached/fallback config synthesized above.
                });
        } else {
            triggerGetAccount()
                .unwrap()
                .then((res) => {
                    if ('onboardingPending' in res) return;
                    applyTenant(res.tenant);
                })
                .catch(() => {
                    // Keep the cached/fallback config synthesized above.
                });
        }

        return () => {
            cancelled = true;
        };
    }, [tenant, isMaster]);

    // /signup is owned by SignupGate, which decides — based on the
    // ?solution param — whether to render the unsupported screen or
    // kick off the Auth0 signup redirect. Render it here, above the
    // generic auth gates, so it survives the unauthenticated state
    // (the SignInRedirectPage branch below would otherwise
    // short-circuit it).
    if (window.location.pathname === paths.signup) {
        return <SignupGate />;
    }

    if (loading) {
        return <LoadingPage />;
    }

    // Signed out, or signed in on a session Auth0 has stopped renewing: either
    // way the user waits for Auth0 here, and the screen offers a manual retry
    // and sign-out if the redirect does not go through.
    if (!isAuthenticated || sessionExpired) {
        return <SignInRedirectPage error={authError} />;
    }

    const getDefaultRedirectPath = () => {
        if (isReceptionist && tenant === 'neuland') {
            return paths.issueReports;
        }
        // Landing is permission-based: Service has no read:dashboard, so it
        // falls through to bookings (its first accessible surface) rather than
        // landing on a dashboard it can't view. Owner/Manager still land on the
        // dashboard via the permission check below.
        if (hasShowDashboardPermission) return paths.dashboard;
        if (hasShowBookingsPermission) return paths.bookings;
        if (hasShowIssueReportsPermission) return paths.issueReports;
        if (hasShowLockersPermission) return paths.depots;
        return paths.unauthorized;
    };

    if (!permissionLoaded) {
        return <LoadingPage />;
    }

    // Layer-2 email-verification gate. The real enforcement lives in
    // Auth0's post-login Action; this branch just gives unverified
    // visitors a friendly in-app screen instead of Auth0's generic
    // error. Triggers when we have an authenticated session but the
    // ID token still says email_verified=false (e.g. user signed up
    // but hasn't clicked the link in their inbox yet).
    const needsEmailVerification =
        isAuthenticated && user && user.email_verified === false;

    // Guard a route element behind a permission: deep-linking a restricted role
    // (e.g. Service, which lacks create/edit bookings and asset management) to a
    // create/edit action lands on Unauthorized instead of the action page.
    const requirePermission = (allowed: boolean, element: JSX.Element) =>
        (allowed ? element : <Navigate to={paths.unauthorized} replace />);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <PostLoginRedirect />
            {needsEmailVerification ? (
                <Routes>
                    <Route path={'*'} element={<VerifyEmailPage />} />
                </Routes>
            ) : tenant ? (
                <Routes>
                    <Route path={'/'} element={<MainLayout />}>
                        <Route
                            path={paths.dashboard}
                            element={
                                hasShowDashboardPermission ? (
                                    <Dashboard />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.depots}
                            element={
                                /* The `tenant !== 'neuland'` exclusion this
                                   guard used to carry is gone: a hardcoded
                                   tenant name was the band-aid for gating on
                                   `read:lockers`, which every self-service
                                   role holds. The entitlement now answers it
                                   for every tenant, Neuland included.

                                   Not entitled is not a refusal: it opens the
                                   page describing the add-on and how to get it
                                   (ADR-0001 Amendment 4). Nothing is granted by
                                   arriving here — every device endpoint behind
                                   the page stays entitlement-gated server-side. */
                                ACCESS_MANAGEMENT_VIEWS[accessManagementCurrentView]()
                            }
                        />
                        <Route
                            path={paths.rentalBooking}
                            element={
                                hasShowBookingsPermission ? (
                                    <BookingsArea />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.rentalIssueReports}
                            element={
                                hasShowIssueReportsPermission ? (
                                    <IssueReports />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />                        
                        <Route
                            path={paths.rentalAddIssueReports}
                            element={
                                hasCreateIssueReportsPermission ? (
                                    <ApartmentRentalAddIssueReportPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.rentalViewIssueReports}
                            element={
                                hasShowIssueReportsPermission ? (
                                    <ApartmentRentalIssueReportDetailsPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        
                        <Route path={paths.rentalApartment} element={<Apartment />} />
                        <Route path={paths.rentalAddOnServices} element={<AddOnServices />} />
                        <Route path={paths.rentalPolices} element={<Polices />} />
                        <Route
                            path={paths.locations}
                            element={
                                tenant !== 'neuland' ? (
                                    <LocationsPage />
                                ) : (
                                    <Navigate to={paths.dashboard} replace />
                                )
                            }
                        />
                        <Route path={paths.depotDetails} element={<DepotDetailsPage />} />
                        <Route
                            path={paths.locationDetails}
                            element={
                                tenant !== 'neuland' ? (
                                    <LocationDetailsPage />
                                ) : (
                                    <Navigate to={paths.dashboard} replace />
                                )
                            }
                        />
                        <Route path={paths.addVisitorBooking} element={<VisitorBookingPage />} />
                        <Route
                            path={paths.addIssueReports}
                            element={
                                hasCreateIssueReportsPermission ? (
                                    <AddIssueReportPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.addIssueReportForBooking}
                            element={
                                hasCreateIssueReportsPermission ? (
                                    <AddIssueReportPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.issueReports}
                            element={
                                hasShowIssueReportsPermission ? (
                                    <IssueReportPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.issueReportDetails}
                            element={
                                hasShowIssueReportsPermission ? (
                                    <IssueReportDetailsPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />                        
                        <Route
                            path={paths.bookings}
                            element={
                                hasShowBookingsPermission ? (
                                    <BookingsArea />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.bookingSearch}
                            element={
                                hasShowBookingsPermission ? (
                                    <BookingsArea />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route path={paths.bookingDetailsSearch} element={<SearchBookingPage />} />
                        <Route path={paths.rentalBookingDetails} element={<BookingDetailsPage />} />
                        <Route path={paths.roomBookingEdit} element={requirePermission(hasEditBookingsPermission, <EditRoomBooking />)} />
                        <Route path={paths.visitorBookings} element={<VisitorBookingsPage />} />
                        <Route path={paths.unauthorized} element={<Unauthorized />} />
                        <Route
                            path={paths.statistics}
                            element={
                                <Suspense fallback={null}>
                                    <StatisticsPage />
                                </Suspense>
                            }
                        />
                        <Route
                            path={paths.vouchersCodes}
                            element={
                                hasReadVouchersPermission ? (
                                    <VouchersCodesPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        {/* Permission only: whether kolula issues this tenant's
                            invoices is answered by the list itself (its `scope`,
                            or 409 `legacy_store`), so a deep link says why it is
                            empty instead of bouncing while the account loads. */}
                        <Route path={paths.invoices} element={requirePermission(hasReadInvoicesPermission, <InvoicesPage />)} />
                        <Route
                            path={paths.rentalProducts}
                            element={
                                // Config loads async after `tenant` is set — render
                                // nothing until it resolves so a deep link isn't
                                // bounced to unauthorized (account-route pattern).
                                !tenantConfig ? null : canSeeServices ? (
                                    <ServicesPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route
                            path={paths.deposits}
                            element={
                                // Same SUP-only gate (and async-config guard) as
                                // the rental-products config surface above.
                                !tenantConfig ? null : hasSupService ? (
                                    <DepositsPage />
                                ) : (
                                    <Navigate to={paths.unauthorized} replace />
                                )
                            }
                        />
                        <Route path={paths.createRoomBooking} element={requirePermission(hasCreateBookingsPermission, <CreateRoomBookingPage />)} />
                        <Route path={paths.createVenueBooking} element={requirePermission(hasCreateBookingsPermission, <CreateVenueBookingPage />)} />
                        {/* Sports halls book on the venue screen, parameterized by use case. */}
                        <Route
                            path={paths.createHallBooking}
                            element={requirePermission(hasCreateBookingsPermission, <CreateVenueBookingPage useCase={'hall'} />)}
                        />
                        {/* One generalized create flow for the non-room use cases,
                            parameterized by use case (Step 2, plan #1432). */}
                        <Route
                            path={paths.createStorageBooking}
                            element={requirePermission(hasCreateBookingsPermission, <CreateNonRoomBookingPage useCase={'storage'} />)}
                        />
                        <Route
                            path={paths.createRentalBooking}
                            element={requirePermission(hasCreateBookingsPermission, <CreateNonRoomBookingPage useCase={'rental'} />)}
                        />
                        <Route
                            path={paths.createSupBooking}
                            element={requirePermission(hasCreateBookingsPermission, <CreateNonRoomBookingPage useCase={'sup'} />)}
                        />
                        <Route path={paths.createSeriesBooking} element={requirePermission(hasCreateBookingsPermission, <CreateSeriesBookingPage />)} />
                        <Route path={paths.seriesBookingDetails} element={<SeriesBookingDetailsPage />} />
                        <Route path={paths.editVenueBooking} element={requirePermission(hasEditBookingsPermission, <EditVenueBookingPage />)} />
                        <Route path={paths.createBlocker} element={requirePermission(hasCreateBookingsPermission, <CreateBlockerPage />)} />
                        <Route path={paths.assets} element={<AssetsOverviewPage />} />
                        <Route path={paths.createAsset} element={requirePermission(hasManageAssetsPermission, <AssetCreatePage />)} />
                        <Route path={paths.createStation} element={requirePermission(hasManageAssetsPermission, <CreateStationPage key={'station'} />)} />
                        <Route
                            path={paths.createHouse}
                            element={requirePermission(hasManageAssetsPermission, <CreateStationPage key={'house'} variant={'house'} />)}
                        />
                        {/* The station AFTER it exists. Same gate as the
                            wizard and the object pages — deliberately NOT the
                            access-management entitlement: a station is
                            inventory and exists whether or not the tenant holds
                            the smart-lock add-on (PO, 2026-09-10). */}
                        <Route path={paths.stationDetails} element={requirePermission(hasReadDepotsPermission, <StationDetailPage />)} />
                        {/* Product detail (asset side). Held like the creation
                            wizard — permission-gated, direct route only, not in the
                            sidebar, while #1712 / #2134 are outstanding. */}
                        <Route path={paths.productDetails} element={requirePermission(hasManageProductsPermission, <ProductDetailPage />)} />
                        <Route path={paths.assetDetails} element={<AssetDetailPage />} />
                        {/* Customers — same gate as the sidebar entry
                            (SideBarContent), incl. deep links. These four had
                            NO guard: on a legacy tenant the pages rendered and
                            every request behind them 409'd, so the screen was
                            reachable and simply broken. `customerAccessGate`
                            renders nothing while the tenant is still resolving
                            — the account-route pattern — so a deep link is not
                            bounced to unauthorized before it is known. */}
                        <Route path={paths.customers} element={customerAccessGate(<CustomersPage />)} />
                        <Route
                            path={paths.customerOrganizationCreate}
                            element={customerAccessGate(<OrganizationCreatePage />)}
                        />
                        {/* The organisation import writes organisations: customers:write, as the route itself requires. */}
                        <Route
                            path={paths.customerOrganizationImport}
                            element={customerAccessGate(requirePermission(hasWriteCustomersPermission, <OrganizationImportPage />))}
                        />
                        <Route
                            path={paths.customerOrganizationDetail}
                            element={customerAccessGate(<OrganizationDetailPage />)}
                        />
                        <Route
                            path={paths.customerAccountDetail}
                            element={customerAccessGate(<CustomerAccountDetailPage />)}
                        />
                        <Route
                            path={paths.account}
                            element={(
                                <AccountGate resolving={accountAccessResolving} canAccess={canAccessAccount}>
                                    <AccountPage />
                                </AccountGate>
                            )}
                        />
                        {/* Same gate as the account: owner, or master viewing the tenant. */}
                        <Route
                            path={paths.onboardingImport}
                            element={(
                                <AccountGate resolving={accountAccessResolving} canAccess={canAccessAccount}>
                                    <OnboardingImportPage />
                                </AccountGate>
                            )}
                        />
                        {/* Help center (#2685): every signed-in user, no permission gate. */}
                        <Route path={paths.help} element={<HelpCenterPage />} />
                        <Route path={paths.helpArticle} element={<HelpArticlePage />} />
                        <Route path={paths.masterBookings} element={<MasterBookingsPage />} />
                        <Route path={paths.masterProblemReports} element={<MasterProblemReportsPage />} />
                        <Route path={paths.masterLockers} element={<MasterLockersPage />} />
                        <Route path={paths.masterTenants} element={<MasterTenantsPage />} />
                        <Route index={true} element={<DefaultLanding fallbackPath={getDefaultRedirectPath()} />} />
                        {/* #2932: an address that matches nothing used to match
                            NOTHING — the tree rendered no element at all and
                            `#root` stayed empty, so a stale bookmark or a typo
                            was indistinguishable from the app being down. Last
                            in the block and inside the layout route, so the
                            navigation is still there. */}
                        <Route path={'*'} element={<NotFound />} />
                        {/* <Route
                            index={true}
                            element={
                                <Navigate
                                    to={
                                        locations.length > 1
                                            ? paths.locations
                                            : paths.depots
                                    }
                                    replace={true}
                                />
                            }
                        /> */}
                    </Route>
                </Routes>
            ) : (
                <Routes>
                    <Route path={paths.onboarding} element={<OnboardingPage />} />
                    <Route path={'*'} element={<TenantBootstrap />} />
                </Routes>
            )}
        </BrowserRouter>
    );
};

export default App;
